import type { AppointmentStatus } from "./types";
import { HOURS } from "./window";

/**
 * The Scheduling agent's decision function (Agent Brains section 5.4).
 *
 * Section 5.4's charter, verbatim: mission "Consults booked, confirmed, and
 * attended."; never "Never books outside your real availability. Never
 * double-books a bench slot."; handoff "Receives from Routing. Hands
 * attendance back to Analysis."; produces "A confirmed appointment and its
 * reminders."; blockedBy "Needs a calendar integration and your real
 * availability. Booking against a guess is worse than not booking."
 *
 * The `blockedBy` line names a real, unsolved problem: nothing in this
 * codebase knows whether the bench has room at any given moment, and this
 * file does not pretend otherwise. It never decides WHETHER to book, WHEN a
 * slot is free, or WHETHER two appointments collide. Those questions stay
 * open, on purpose (CLAUDE.md section 0: precision beats recall, and a
 * confident guess about availability is worse than no booking at all).
 *
 * What this file DOES decide, given an appointment that a human already
 * created with a real date and time they know is free
 * (`packages/db/src/appointments.ts`'s `createAppointment`, always a human
 * action, never inferred from message content): whether right now is the
 * moment to remind that contact it is coming up. This is the same
 * pure-decision-plus-machinery split `packages/core/src/reviewAsk.ts` and
 * `packages/core/src/followUp.ts` already use; the machinery that finds
 * candidates and drives an eligible reminder through the real send path sits
 * in `packages/agents/src/appointmentReminderSweep.ts`.
 */

/**
 * How long before the appointment the first reminder fires.
 *
 * One vault figure exists near this problem, `STANDARD_WINDOW_MS` (Meta's 24h
 * messaging window, T3, never agent-tunable), but that number answers a
 * different question: how long a customer's last message keeps a channel
 * open, not how far ahead of an appointment a reminder reads as useful rather
 * than premature. Section 5.4 names no reminder cadence at all, so this is an
 * implementer's placeholder in the exact shape `FOLLOWUP_SILENCE_MS`
 * (followUp.ts) and `REVIEW_ASK_DELAY_MS` (reviewAsk.ts) already carry: a day
 * ahead is long enough that a customer can still rearrange their schedule if
 * something is wrong, short enough that it reads as "this is coming up," not
 * "why are you emailing me about something three weeks out."
 * AWAITING RATIFICATION (TA-185): unmeasured against real bookings.
 */
export const APPOINTMENT_REMINDER_DAY_BEFORE_MS = 24 * HOURS;

/**
 * How long before the appointment the second, final reminder fires.
 *
 * Same AWAITING RATIFICATION status. An hour is long enough to still be
 * useful (leave now, grab the piece you were bringing in) and short enough
 * that it reads as a same-day nudge, not a repeat of the day-before message.
 */
export const APPOINTMENT_REMINDER_HOUR_BEFORE_MS = 1 * HOURS;

/**
 * The only two states an appointment can be reminded about. `booked` and
 * `confirmed` both still lie ahead of the shop; every other status is either
 * a resolved appointment (`attended`, `no_show`) or one that will never
 * happen (`cancelled`), and a reminder about any of those would be reminding
 * a customer of something that is no longer true, the exact failure section 0
 * calls out.
 */
export const APPOINTMENT_REMINDER_UPCOMING_STATUSES: ReadonlySet<AppointmentStatus> = new Set([
  "booked",
  "confirmed",
]);

export type AppointmentReminderKind = "day_before" | "hour_before";

export type AppointmentReminderSkipReason =
  | "not_upcoming_status"
  | "too_early"
  | "past"
  | "day_before_already_sent"
  | "hour_before_already_sent";

export type AppointmentReminderDecision =
  | { readonly due: true; readonly kind: AppointmentReminderKind }
  | { readonly due: false; readonly reason: AppointmentReminderSkipReason };

export interface AppointmentReminderInput {
  /** Kept as `AppointmentStatus | string`, the same forward-compatible shape
   * `ReviewAskInput.outcome` uses: an unrecognised value fails closed into
   * `not_upcoming_status` rather than throwing. */
  readonly status: AppointmentStatus | string;
  readonly scheduledAt: Date;
  readonly now: Date;
  /** A day-before reminder already went out for THIS appointment. */
  readonly dayBeforeSent: boolean;
  /** An hour-before reminder already went out for THIS appointment. */
  readonly hourBeforeSent: boolean;
}

function skip(reason: AppointmentReminderSkipReason): AppointmentReminderDecision {
  return { due: false, reason };
}

/**
 * Section 0: precision beats recall. Nearest tier first, so a late-booked
 * appointment (created inside the day-before window, or even inside the
 * hour-before one) goes straight to whichever reminder still fits rather than
 * being stuck waiting on a day-before slot that has already effectively
 * passed. Once time has closed in past the hour-before mark, the day-before
 * message is moot: sending it that late would misdescribe the appointment as
 * further away than it is, so a missed day-before reminder is never revived
 * once the hour-before window has arrived. Every branch that could go either
 * way resolves toward NOT reminding, the same discipline `decideReviewAsk`
 * and `planFollowUp` already hold their whole shape to.
 */
export function decideAppointmentReminder(
  input: AppointmentReminderInput,
): AppointmentReminderDecision {
  if (!APPOINTMENT_REMINDER_UPCOMING_STATUSES.has(input.status as AppointmentStatus)) {
    return skip("not_upcoming_status");
  }

  const timeUntil = input.scheduledAt.getTime() - input.now.getTime();

  // The appointment's own moment has arrived or gone by, and it is still
  // `booked`/`confirmed`: nobody has recorded whether the customer showed.
  // Never inferred here, or anywhere in this codebase (attendance is a human
  // action, packages/db/src/appointments.ts's `recordAttendance`), but the
  // sweep raises an alert on exactly this branch so a human is asked, loudly,
  // rather than the appointment sitting unresolved forever (CLAUDE.md
  // section 0: fail loudly).
  if (timeUntil <= 0) {
    return skip("past");
  }

  if (timeUntil <= APPOINTMENT_REMINDER_HOUR_BEFORE_MS) {
    return input.hourBeforeSent
      ? skip("hour_before_already_sent")
      : { due: true, kind: "hour_before" };
  }

  if (timeUntil <= APPOINTMENT_REMINDER_DAY_BEFORE_MS) {
    return input.dayBeforeSent
      ? skip("day_before_already_sent")
      : { due: true, kind: "day_before" };
  }

  return skip("too_early");
}
